'use client';

import type { ButtonHTMLAttributes, JSX } from 'react';
import React, { forwardRef } from 'react';

export type IconButtonVariant =
  | 'primary'
  | 'secondary'
  | 'primaryTransparent'
  | 'secondaryTransparent'
  | 'redTransparent';

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  icon?: JSX.Element | React.ReactNode;
  variant?: IconButtonVariant;
  disableTranslate?: boolean;
  keepTextHeight?: boolean;
  dictionary?: {
    buttons: Record<string, string>;
  };
}

const variants = {
  primary: {
    bg: 'bg-gradient-to-bl from-primary-400 to-primary-500 disabled:bg-primary-200',
    text: 'text-white',
  },
  secondary: {
    bg: 'bg-primary-950',
    text: 'text-white',
  },
  primaryTransparent: {
    bg: 'bg-transparent border-transparent !shadow-none p-1.5',
    text: 'text-primary-500',
  },
  secondaryTransparent: {
    bg: 'bg-transparent border-transparent !shadow-none p-1.5',
    text: 'text-white',
  },
  redTransparent: {
    bg: 'bg-transparent border-transparent !shadow-none p-1.5',
    text: 'text-red-600',
  },
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      disableTranslate = false,
      keepTextHeight = false,
      icon,
      variant = 'primary',
      className,
      dictionary,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={`flex flex-row items-center justify-center gap-2 rounded-full px-8 py-3 font-semibold shadow-md transition duration-200 ease-in-out focus:outline-none focus:ring-0 ${variants[variant].bg} ${className}`}
        {...props}
      >
        {!(!keepTextHeight && !label) && (
          <span
            className={`pointer-events-none inline-block w-max grow text-center ${variants[variant].text}`}
          >
            {!disableTranslate && label && dictionary?.buttons
              ? (dictionary.buttons[label] ?? label)
              : label}
          </span>
        )}
        {icon && (
          <span className={`pointer-events-none ${variants[variant].text}`}>
            {icon}
          </span>
        )}
      </button>
    );
  }
);

export default IconButton;
